import { ModalFuncProps } from 'antd';
import { ModalProps } from 'components/styled/modal/Modal';
import { useIsMounted } from 'hooks';
import { useCallback, useMemo, useState } from 'react';

export interface UseModalReturnType<T> {
  data?: T;
  visible: boolean;
  open: (data?: T) => void;
  close: () => void;
  modalProps: Omit<ModalFuncProps, 'onCancel'> & { onCancel: () => void };
}

interface UseModalProps<T> {
  onOpen?: (data?: T) => void;
  onClose?: () => void;
}

const useModal = <T>(props?: UseModalProps<T>): UseModalReturnType<T> => {
  const { onOpen, onClose } = props || {};

  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<T>();

  const isMounted = useIsMounted();

  const open = useCallback(
    (data?: T) => {
      if (!isMounted()) return;

      setData(data);
      if (!visible) setVisible(true);
      onOpen?.(data);
    },
    [isMounted, onOpen, visible],
  );

  const close = useCallback(() => {
    if (!isMounted()) return;
    onClose?.();
    setVisible(false);

    setData(undefined);
  }, [isMounted, onClose]);

  const modalProps = useMemo(
    () =>
      ({
        onCancel: () => close(),
        open: visible,
        data: data,
      }) satisfies ModalProps & { data?: T },
    [close, data, visible],
  );

  const state: UseModalReturnType<T> = useMemo(
    () => ({
      data,
      visible,
      open,
      close,
      onOpen,
      onClose,
      modalProps,
    }),
    [data, visible, open, close, onOpen, onClose, modalProps],
  );

  return state;
};

export default useModal;
